/**
 * WorkspaceJoinedCallout (Figma MVP-DESIGNES 10216:4361 one workspace,
 * 10219:22441 several).
 *
 * The "Welcome aboard!" bubble shown right after the user accepted
 * invitation(s) from the in-app modal: the avatar menu opens and the rows of
 * the workspace(s) they just joined are framed by a 2px blue outline, with
 * the bubble to the left of the menu pointing at the frame.
 *
 * It is rendered *inside* the menu panel (`.home-nav-user-menu-panel`) and
 * measures the rows against that panel, so it shares the menu's stacking
 * context instead of fighting the navbar's z-index from a portal, and it
 * follows the menu if the navbar moves.  Same blue Spark `Tooltip` chrome as
 * the walkthrough coach marks.  Not modal: `Close` hides the bubble and the
 * frame; the menu stays open.
 */
import React, { useEffect, useState } from 'react';
import { Button, Text, Tooltip } from '@spark';
import { workspaceRowId } from './WorkspaceSwitcher';
import './WorkspaceJoinedCallout.css';

export const WORKSPACE_JOINED_TITLE = 'Welcome aboard!';
export const WORKSPACE_JOINED_CLOSE_LABEL = 'Close';
/** How far the outline sits outside the rows' box (Figma: 280 wide around a 264 row). */
export const WORKSPACE_JOINED_FRAME_OFFSET = 8;
/** Gap between the bubble's tip and the frame. */
const FRAME_TO_BUBBLE_GAP = 6;

export const workspaceJoinedBody = (count) =>
    count > 1
        ? 'Your new workspaces are ready. Use this menu to navigate between your different teams and projects anytime.'
        : 'Your new workspace is ready. Use this menu to navigate between your different teams and projects anytime.';

/** Union of the rows' boxes in the panel's coordinate space, or null when none is in the DOM. */
export function measureRows(panel, workspaceIds) {
    if (!panel) return null;
    const panelBox = panel.getBoundingClientRect();
    const boxes = workspaceIds
        .map((id) => document.getElementById(workspaceRowId(id)))
        .filter((el) => el && panel.contains(el))
        .map((el) => el.getBoundingClientRect());
    if (boxes.length === 0) return null;
    const top = Math.min(...boxes.map((b) => b.top)) - panelBox.top;
    const bottom = Math.max(...boxes.map((b) => b.bottom)) - panelBox.top;
    const left = Math.min(...boxes.map((b) => b.left)) - panelBox.left;
    const right = Math.max(...boxes.map((b) => b.right)) - panelBox.left;
    return { top, left, width: right - left, height: bottom - top };
}

export default function WorkspaceJoinedCallout({ panelRef, workspaceIds = [], onClose }) {
    const [frame, setFrame] = useState(null);

    // A passive effect: the panel's ref is attached after this child's layout
    // effects would run, so measuring there would always see a null panel.
    useEffect(() => {
        const panel = panelRef?.current;
        const measure = () => setFrame(measureRows(panel, workspaceIds));
        measure();
        if (!panel || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(panel);
        return () => observer.disconnect();
    }, [panelRef, workspaceIds]);

    if (!frame || workspaceIds.length === 0) return null;

    const offset = WORKSPACE_JOINED_FRAME_OFFSET;
    const frameStyle = {
        top: frame.top - offset,
        left: frame.left - offset,
        width: frame.width + offset * 2,
        height: frame.height + offset * 2,
    };
    const bubbleStyle = {
        top: frameStyle.top + frameStyle.height / 2,
        right: `calc(100% - ${frameStyle.left - FRAME_TO_BUBBLE_GAP}px)`,
    };

    return (
        <>
            <div className="workspace-joined-callout__frame" style={frameStyle} aria-hidden="true" />
            <div
                className="workspace-joined-callout"
                style={bubbleStyle}
                role="status"
                data-testid="workspace-joined-callout"
            >
                <Tooltip type="slot" variant="dark" tipDirection="left-center" className="workspace-joined-callout__tooltip">
                    <div className="workspace-joined-callout__copy">
                        <Text as="p" variant="heading-5-semibold" className="workspace-joined-callout__title">
                            {WORKSPACE_JOINED_TITLE}
                        </Text>
                        <Text as="p" variant="lead-regular" className="workspace-joined-callout__body">
                            {workspaceJoinedBody(workspaceIds.length)}
                        </Text>
                    </div>
                    <div className="workspace-joined-callout__footer">
                        <Button size="tiny" variant="primary" className="workspace-joined-callout__close" onClick={onClose}>
                            {WORKSPACE_JOINED_CLOSE_LABEL}
                        </Button>
                    </div>
                </Tooltip>
            </div>
        </>
    );
}
